import React, { useEffect } from "react";

export default function SecurityAlertModal({
  isOpen = true,
  onProceed,
  onCancel,
}) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/75 backdrop-blur-md p-3 sm:p-4"
      aria-modal="true"
      role="dialog"
    >
      <style>{`
        .custom-modal-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .custom-modal-scroll::-webkit-scrollbar-track {
          background: #0F2128;
          border-radius: 8px;
        }
        .custom-modal-scroll::-webkit-scrollbar-thumb {
          background: #2D5F7A;
          border-radius: 8px;
        }
        .custom-modal-scroll::-webkit-scrollbar-thumb:hover {
          background: #7BC4E0;
        }
      `}</style>

      <div
        className="relative w-full max-w-xl bg-[#1A2F3A] rounded-2xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.85)] overflow-hidden flex flex-col my-auto border border-[#2D5F7A]/50"
        style={{ maxHeight: "90vh" }}
      >
        {/* Soft background glow elements matching the SalaryMates theme */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#7BC4E0]/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-[#2D5F7A]/25 rounded-full blur-3xl" />
        </div>

        {/* Header with dark teal-cyan gradient matching theme */}
        <div className="px-5 py-4 sm:px-6 sm:py-5 flex items-center justify-between shrink-0 relative z-10 bg-gradient-to-r from-[#0F2128] via-[#142A35] to-[#1A3847] border-b border-[#2D5F7A]/40">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#7BC4E0]/15 border border-[#7BC4E0]/30 flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(123,196,224,0.2)]">
              <svg
                className="w-6 h-6 sm:w-7 sm:h-7 text-[#7BC4E0]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-white tracking-wide">
                Security Alert
              </h3>
              <p className="text-xs sm:text-sm font-semibold text-[#7BC4E0] tracking-wider mt-0.5">
                सुरक्षा चेतावनी
              </p>
            </div>
          </div>

          {/* Close button */}
          <button
            type="button"
            onClick={onProceed}
            aria-label="Close"
            className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/15 text-slate-300 hover:text-white border border-[#2D5F7A]/40 flex items-center justify-center transition-all shrink-0 cursor-pointer"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth="2.5"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Modal Content */}
        <div className="px-5 py-5 sm:px-6 sm:py-6 overflow-y-auto space-y-4 text-left relative z-10 custom-modal-scroll">
          {/* Main warning headline */}
          <div className="p-3.5 rounded-xl bg-[#0F2128]/70 border border-[#2D5F7A]/30">
            <p className="font-semibold text-white text-sm sm:text-base leading-snug">
              Fraudsters are sending fake payment requests via WhatsApp, SMS, and
              social media, impersonating Salarymates employees. Stay alert:
            </p>
            <p className="text-[#CCE6F5]/85 text-xs sm:text-sm leading-relaxed mt-1.5">
              धोखेबाज़ WhatsApp, SMS और सोशल मीडिया के माध्यम से नकली भुगतान अनुरोध
              भेज रहे हैं, जो Salarymates के कर्मचारियों के रूप में पहचान बना
              रहे हैं। सतर्क रहें:
            </p>
          </div>

          {/* Key Guidelines with themed cyan icons */}
          <div className="space-y-3 pt-1">
            {/* Item 1 */}
            <div className="flex items-start gap-3 p-3 rounded-lg bg-[#0F2128]/40 border border-[#2D5F7A]/20">
              <svg
                className="w-5 h-5 text-[#7BC4E0] shrink-0 mt-0.5 drop-shadow-[0_0_6px_rgba(123,196,224,0.35)]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <div>
                <p className="font-semibold text-white text-sm sm:text-base leading-tight">
                  Do not click unknown links or reply to suspicious messages.
                </p>
                <p className="text-[#8FAFC7] text-xs sm:text-sm mt-0.5 leading-snug">
                  अज्ञात लिंक पर क्लिक न करें या संदिग्ध संदेशों का जवाब न दें।
                </p>
              </div>
            </div>

            {/* Item 2 */}
            <div className="flex items-start gap-3 p-3 rounded-lg bg-[#0F2128]/40 border border-[#2D5F7A]/20">
              <svg
                className="w-5 h-5 text-[#7BC4E0] shrink-0 mt-0.5 drop-shadow-[0_0_6px_rgba(123,196,224,0.35)]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <div>
                <p className="font-semibold text-white text-sm sm:text-base leading-tight">
                  Verify email IDs and domains before engaging.
                </p>
                <p className="text-[#8FAFC7] text-xs sm:text-sm mt-0.5 leading-snug">
                  संलग्न होने से पहले ईमेल ID और डोमेन सत्यापित करें।
                </p>
              </div>
            </div>

            {/* Item 3 */}
            <div className="flex items-start gap-3 p-3 rounded-lg bg-[#0F2128]/40 border border-[#2D5F7A]/20">
              <svg
                className="w-5 h-5 text-[#7BC4E0] shrink-0 mt-0.5 drop-shadow-[0_0_6px_rgba(123,196,224,0.35)]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <div>
                <p className="font-semibold text-white text-sm sm:text-base leading-tight">
                  Be cautious of calls or messages claiming to be from us.
                </p>
                <p className="text-[#8FAFC7] text-xs sm:text-sm mt-0.5 leading-snug">
                  हमारे होने का दावा करने वाले कॉल या संदेशों से सावधान रहें।
                </p>
              </div>
            </div>
          </div>

          {/* Red Warning Card (Disclaimer) in Dark Theme */}
          <div className="bg-[#ef4444]/10 border-l-4 border-red-500 border-t border-r border-b border-red-500/20 rounded-r-xl rounded-l-sm p-4 my-2 shadow-[0_0_15px_rgba(239,68,68,0.08)]">
            <p className="font-bold text-red-400 text-sm sm:text-base leading-snug">
              Salarymates is not responsible for losses from payments to
              unverified persons or links.
            </p>
            <p className="text-red-300/85 text-xs sm:text-sm mt-1 leading-relaxed">
              Salarymates अप्रमाणित व्यक्तियों या लिंक को भुगतान से होने वाले
              नुकसान के लिए जिम्मेदार नहीं है।
            </p>
          </div>

          {/* Safe Payment / सुरक्षित भुगतान Section in Dark Theme */}
          <div className="space-y-2 pt-1">
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5 text-emerald-400 shrink-0 drop-shadow-[0_0_8px_rgba(52,211,153,0.35)]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="9 11 12 14 22 4" />
              </svg>
              <h4 className="font-bold text-base sm:text-lg text-emerald-400 tracking-wide">
                Safe Payment / सुरक्षित भुगतान
              </h4>
            </div>

            <div className="bg-[#064e3b]/25 border border-emerald-500/30 rounded-xl p-4 space-y-3 shadow-[0_0_15px_rgba(16,185,129,0.08)]">
              <div>
                <p className="font-bold text-emerald-300 text-sm sm:text-base leading-snug">
                  Pay only through our official website Salarymates or authorized
                  link.
                </p>
                <p className="text-emerald-400/85 text-xs sm:text-sm mt-0.5 leading-snug">
                  भुगतान केवल हमारी आधिकारिक वेबसाइट Salarymates या अधिकृत लिंक
                  से करें।
                </p>
              </div>

              <div>
                <p className="font-bold text-emerald-300 text-sm sm:text-base leading-snug">
                  Do not trust any unknown number/link.
                </p>
                <p className="text-emerald-400/85 text-xs sm:text-sm mt-0.5 leading-snug">
                  किसी अज्ञात नंबर/लिंक पर भरोसा न करें।
                </p>
              </div>
            </div>
          </div>

          {/* Signoff */}
          <div className="text-right pt-2 pb-1">
            <p className="italic font-bold text-[#7BC4E0] text-sm sm:text-base">
              - Team Salarymates
            </p>
          </div>
        </div>

        {/* Footer Actions in Dark Theme */}
        <div className="border-t border-[#2D5F7A]/40 bg-[#0F2128] px-5 py-3.5 sm:px-6 sm:py-4 flex items-center justify-end gap-3 shrink-0 relative z-10">
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2 sm:px-6 sm:py-2.5 rounded-lg border border-[#2D5F7A]/50 bg-[#162A36] text-[#CCE6F5] font-semibold hover:bg-[#1E3B4A] hover:text-white transition-all shadow-sm text-sm sm:text-base active:scale-[0.98] cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onProceed}
            className="px-5 py-2 sm:px-6 sm:py-2.5 rounded-lg bg-gradient-to-r from-[#207594] to-[#2ea4cf] hover:from-[#2786a8] hover:to-[#38b5e6] text-white font-bold transition-all shadow-[0_0_20px_rgba(46,164,207,0.35)] text-sm sm:text-base active:scale-[0.98] cursor-pointer"
          >
            Proceed to Repay / आगे बढ़ें
          </button>
        </div>
      </div>
    </div>
  );
}
